import React from "react";
import { Modal, Form, Select, Input, InputNumber, Space, Button } from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;

// Generate academic years (single year format, e.g. "2026")
const currentYear = new Date().getFullYear();
const academicYears = Array.from({ length: 6 }, (_, i) =>
  String(currentYear + i),
);

const CreateBudgetModal = ({
  visible,
  onCancel,
  onSubmit,
  departments,
  loading,
}) => {
  const [form] = Form.useForm();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Keep fiscal year and academic year in sync (both use single year format)
      values.acadyr = values.fiscalYear;
      const success = await onSubmit(values);
      if (success) {
        form.resetFields();
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <UnorderedListOutlined />
          <span>Create New Budget</span>
        </Space>
      }
      open={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Create Budget
        </Button>,
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          fiscalYear: String(currentYear),
        }}
      >
        <Form.Item
          name="departmentId"
          label="Item"
          rules={[{ required: true, message: "Please select an item" }]}
        >
          <Select placeholder="Select item" showSearch optionFilterProp="label">
            {departments.map((d) => (
              <Option
                key={d.departmentId}
                value={d.departmentId}
                label={`${d.name} ${d.code || ""} ${d.description || ""}`}
              >
                {d.name} ({d.code})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="fiscalYear"
          label="Academic Year"
          rules={[{ required: true, message: "Please select academic year" }]}
        >
          <Select placeholder="Select academic year">
            {academicYears.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="term"
          label="Term"
          rules={[{ required: true, message: "Please select a term" }]}
        >
          <Select placeholder="Select term">
            <Option value="T1">Term 1</Option>
            <Option value="T2">Term 2</Option>
            <Option value="T3">Term 3</Option>
          </Select>
        </Form.Item>

        <Form.Item name="budgetCode" label="Budget Code (optional)">
          <Input maxLength={50} placeholder="Enter an optional budget code" />
        </Form.Item>

        <Form.Item
          name="totalAmount"
          label="Total Budget Amount (UGX)"
          rules={[
            { required: true, message: "Please enter total amount" },
            { type: "number", min: 0, message: "Amount must be positive" },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter total budget amount"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/,/g, "")}
            min={0}
          />
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <TextArea rows={3} placeholder="Optional notes about this budget" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateBudgetModal;
